import React from 'react';
import type { BlockRenderProps } from './types';

export const HeadingRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const text = (node.props?.text as string) || 'Headline Title';
  const level = (node.props?.level as string) || 'h2';
  const align = (node.props?.align as 'left' | 'center' | 'right') || 'left';

  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';

  const sizeClasses: Record<string, string> = {
    h1: 'text-4xl font-extrabold tracking-tight',
    h2: 'text-3xl font-bold tracking-tight',
    h3: 'text-2xl font-bold',
    h4: 'text-xl font-semibold',
    h5: 'text-lg font-semibold',
    h6: 'text-base font-semibold',
  };

  const Tag = (level as keyof React.JSX.IntrinsicElements) || 'h2';

  return (
    <Tag className={`${sizeClasses[level] || sizeClasses.h2} ${alignClass} text-foreground mb-3`}>
      {text}
    </Tag>
  );
};

export const ParagraphRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const text = (node.props?.text as string) || 'Descriptive paragraph content goes here.';
  const align = (node.props?.align as 'left' | 'center' | 'right') || 'left';
  const size = (node.props?.size as 'sm' | 'base' | 'lg') || 'base';

  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  const sizeClass = size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base';

  return (
    <p className={`${sizeClass} ${alignClass} text-muted-foreground leading-relaxed mb-3`}>
      {text}
    </p>
  );
};

export const ButtonRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const text = (node.props?.text as string) || 'Click Here';
  const link = (node.props?.link as string) || '#';
  const variant = (node.props?.variant as string) || 'primary';
  const fullWidth = !!node.props?.fullWidth;

  const variantClasses: Record<string, string> = {
    primary: 'bg-primary text-primary-foreground hover:opacity-90 shadow-sm',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border border-input bg-background hover:bg-accent text-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground text-foreground',
  };

  return (
    <div className={`inline-block ${fullWidth ? 'w-full' : ''}`}>
      <a
        href={link}
        className={`inline-flex items-center justify-center font-medium px-5 py-2.5 rounded-lg text-sm transition-colors ${variantClasses[variant] || variantClasses.primary} ${fullWidth ? 'w-full' : ''}`}
      >
        {text}
      </a>
    </div>
  );
};

export const RichTextBlockRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const content = (node.props?.contentHTML as string) || '<p>Rich text block content.</p>';

  return (
    <div
      className="prose prose-sm md:prose-base dark:prose-invert max-w-none mb-4"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

export const MathFormulaBlockRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const latex = (node.props?.latex as string) || '\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}';
  const display = node.props?.display !== false;

  return (
    <div className={`my-4 p-3 bg-muted/40 rounded-lg text-foreground font-mono text-center overflow-x-auto ${display ? 'block' : 'inline-block'}`}>
      <div className="text-xs text-muted-foreground uppercase font-sans tracking-wider mb-1">LaTeX Math Formula</div>
      <div className="text-base font-semibold">{`$$ ${latex} $$`}</div>
    </div>
  );
};
